/**
 * ============================================================================
 * TOAST NOTIFICATION SYSTEM
 * ============================================================================
 *
 * A global toast notification system using React Context.
 * Provides non-blocking feedback for user actions throughout the app.
 *
 * Features:
 * ---------
 * 1. Four toast types: success, error, warning, info
 * 2. Auto-dismiss with configurable duration
 * 3. Manual dismiss via X button
 * 4. Animated entrance/exit with Framer Motion
 * 5. Glassmorphism styling with backdrop blur
 * 6. Positioned top-left to avoid 1Password popup overlap
 *
 * Usage:
 * ------
 * 1. Wrap app in <ToastContainer>
 * 2. In any component: const { toast } = useToast();
 * 3. Call: toast.success('Message'), toast.error('Message'), etc.
 *
 * Example:
 *   const { toast } = useToast();
 *   toast.success('Address added to whitelist!');
 *   toast.error('Invalid address format', { title: 'Validation Error' });
 *
 * @module Toast
 */

import React, { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react';

// React Context for global toast access
const ToastContext = createContext(null);

/**
 * Toast Type Styling Configuration
 * Each type has: icon component, background color, border color, icon color, text color
 * Colors use RGBA for transparency and blend with dark theme
 */
const toastStyles = {
  success: {
    icon: CheckCircle,
    bgColor: 'rgba(16,185,129,0.15)',
    borderColor: 'rgba(16,185,129,0.5)',
    iconColor: '#10b981',
    textColor: '#6ee7b7'
  },
  error: {
    icon: XCircle,
    bgColor: 'rgba(239,68,68,0.15)',
    borderColor: 'rgba(239,68,68,0.5)',
    iconColor: '#ef4444',
    textColor: '#fca5a5'
  },
  warning: {
    icon: AlertTriangle,
    bgColor: 'rgba(245,158,11,0.15)',
    borderColor: 'rgba(245,158,11,0.5)',
    iconColor: '#f59e0b',
    textColor: '#fcd34d'
  },
  info: {
    icon: Info,
    bgColor: 'rgba(96,165,250,0.15)',
    borderColor: 'rgba(96,165,250,0.5)',
    iconColor: '#60a5fa',
    textColor: '#93c5fd'
  }
};

/**
 * Individual Toast Item Component
 * --------------------------------
 * Renders a single toast notification with icon, message, and dismiss button.
 * Auto-dismisses after duration (default 5000ms, 0 = never auto-dismiss).
 *
 * @param {Object} toast - Toast data { id, type, title?, message, duration }
 * @param {Function} onDismiss - Callback to remove toast by id
 */
function ToastItem({ toast, onDismiss }) {
  const style = toastStyles[toast.type] || toastStyles.info;
  const Icon = style.icon;

  // Auto-dismiss timer (skip if duration is 0)
  useEffect(() => {
    if (toast.duration !== 0) {
      const timer = setTimeout(() => onDismiss(toast.id), toast.duration || 5000);
      return () => clearTimeout(timer);
    }
  }, [toast.id, toast.duration, onDismiss]);

  return (
    <motion.div
      initial={{ opacity: 0, x: -100, scale: 0.9 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: -100, scale: 0.9 }}
      transition={{ type: 'spring', damping: 20, stiffness: 300 }}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        padding: '14px 16px',
        background: style.bgColor,
        border: `1px solid ${style.borderColor}`,
        borderRadius: 12,
        backdropFilter: 'blur(12px)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
        minWidth: 280,
        maxWidth: 400
      }}
    >
      <Icon size={20} style={{ color: style.iconColor, flexShrink: 0, marginTop: 2 }} />
      <div style={{ flex: 1 }}>
        {toast.title && (
          <div style={{ color: style.textColor, fontWeight: 600, marginBottom: 4 }}>
            {toast.title}
          </div>
        )}
        <div style={{ color: '#d1d5db', fontSize: '0.9rem', lineHeight: 1.4 }}>
          {toast.message}
        </div>
      </div>
      <button
        onClick={() => onDismiss(toast.id)}
        style={{
          background: 'transparent',
          border: 'none',
          color: '#6b7280',
          cursor: 'pointer',
          padding: 4,
          borderRadius: 4
        }}
      >
        <X size={16} />
      </button>
    </motion.div>
  );
}

/**
 * Toast Container Provider
 * ------------------------
 * Wraps the app and provides toast functionality via Context.
 * Renders toast notifications in a fixed position container.
 *
 * Position: Top-left (to avoid 1Password popup in top-right)
 * Z-index: 999999 (above all other content including modals)
 *
 * @param {React.ReactNode} children - App content to wrap
 */
export function ToastContainer({ children }) {
  // Array of active toasts
  const [toasts, setToasts] = useState([]);

  /**
   * Add a new toast to the stack
   * @returns {number} The toast ID (for manual dismissal)
   */
  const addToast = useCallback((toast) => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { ...toast, id }]);
    return id;
  }, []);

  /**
   * Remove a toast by ID
   */
  const dismissToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  /**
   * Main toast function - creates a toast with options
   * @param {string} message - Toast message
   * @param {Object} options - { type, title, duration }
   */
  const toast = useCallback((message, options = {}) => {
    return addToast({
      message,
      type: options.type || 'info',
      title: options.title,
      duration: options.duration ?? 5000
    });
  }, [addToast]);

  // Convenience methods for common toast types
  toast.success = (message, options = {}) => toast(message, { ...options, type: 'success' });
  toast.error = (message, options = {}) => toast(message, { ...options, type: 'error' });
  toast.warning = (message, options = {}) => toast(message, { ...options, type: 'warning' });
  toast.info = (message, options = {}) => toast(message, { ...options, type: 'info' });

  return (
    <ToastContext.Provider value={{ toast, addToast, dismissToast }}>
      {children}
      <div style={{
        position: 'fixed',
        top: 20,
        left: 20,
        zIndex: 999999,
        display: 'flex',
        flexDirection: 'column',
        gap: 12
      }}>
        <AnimatePresence>
          {toasts.map(t => (
            <ToastItem key={t.id} toast={t} onDismiss={dismissToast} />
          ))}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  );
}

/**
 * useToast Hook
 * -------------
 * Custom hook to access toast functionality from any component.
 * Must be used within a ToastContainer.
 *
 * @returns {{ toast: Function, addToast: Function, dismissToast: Function }}
 *
 * @example
 * const { toast } = useToast();
 * toast.success('Operation completed!');
 * toast.error('Something went wrong', { title: 'Error' });
 * toast.warning('Please check your input');
 * toast.info('Did you know...', { duration: 10000 });
 */
export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastContainer');
  }
  return context;
}
